import { useEffect, useMemo, useState } from "react";
import {
    AlertTriangle, ArrowRightLeft, ArrowUpRight, Building2, ChevronDown, ChevronRight, FileText, Flag, Handshake,
    History as HistoryIcon, Lock, MapPin, Newspaper, ShieldCheck, Store, UserRound,
} from "lucide-react";
import { fmtDate, HistoryFilter, HistoryItem, HistoryKind } from "./model";
import { Section, TONE_CHIP } from "./parts";

const ICONS: Record<HistoryKind, React.ElementType> = {
    creation: Flag,
    opening: Store,
    closing: Lock,
    owner_change: UserRound,
    franchise: Handshake,
    expansion: MapPin,
    multi_site: Building2,
    hygiene: ShieldCheck,
    bodacc_radiation: AlertTriangle,
    bodacc_vente: ArrowRightLeft,
    bodacc_modification: FileText,
    bodacc_creation: Building2,
    bodacc_other: FileText,
    news: Newspaper,
};

const FILTERS: Array<{ key: HistoryFilter; label: string }> = [
    { key: "all", label: "Tout" },
    { key: "activity", label: "Activité" },
    { key: "legal", label: "Juridique" },
    { key: "hygiene", label: "Hygiène" },
    { key: "news", label: "Presse" },
];

const PAGE = 8;

export function History({ items, focus }: { items: HistoryItem[]; focus: { id: string; n: number } | null }) {
    const focusId = focus?.id ?? null;
    const [filter, setFilter] = useState<HistoryFilter>("all");
    const [expanded, setExpanded] = useState(false);
    const [openId, setOpenId] = useState<string | null>(null);

    const counts = useMemo(() => {
        const c: Record<string, number> = { all: items.length };
        items.forEach((i) => { c[i.filter] = (c[i.filter] || 0) + 1; });
        return c;
    }, [items]);

    // A "Pourquoi maintenant" line points here: show everything, open that row, bring it into view.
    useEffect(() => {
        if (!focusId) return;
        setFilter("all");
        setExpanded(true);
        setOpenId(focusId);
        const t = setTimeout(() => document.getElementById(`history-${focusId}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
        return () => clearTimeout(t);
    }, [focus]); // eslint-disable-line react-hooks/exhaustive-deps

    const shown = items.filter((i) => filter === "all" || i.filter === filter);
    const visible = expanded ? shown : shown.slice(0, PAGE);

    return (
        <Section id="historique" title="Historique" icon={HistoryIcon}>
            {items.length === 0 ? (
                <p className="font-sans text-sm text-ink-muted">Aucun événement daté pour cet établissement.</p>
            ) : (
                <>
                    <div className="mb-4 flex flex-wrap gap-1.5" role="tablist" aria-label="Filtrer l'historique">
                        {FILTERS.filter((f) => counts[f.key]).map((f) => (
                            <button
                                key={f.key}
                                type="button"
                                role="tab"
                                aria-selected={filter === f.key}
                                onClick={() => setFilter(f.key)}
                                className={`inline-flex h-8 items-center gap-1.5 rounded-full px-3 font-sans text-sm transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                                    filter === f.key ? "bg-ink text-ink-inverse" : "bg-surface-sunken text-ink-muted hover:text-ink"
                                }`}
                            >
                                {f.label}
                                <span className="font-mono text-xs opacity-70">{counts[f.key]}</span>
                            </button>
                        ))}
                    </div>

                    <ol className="relative">
                        <span aria-hidden className="absolute bottom-4 left-4 top-4 w-px bg-border" />
                        {visible.map((it) => (
                            <HistoryRow
                                key={it.id}
                                item={it}
                                open={openId === it.id}
                                focused={focusId === it.id}
                                onToggle={() => setOpenId((cur) => (cur === it.id ? null : it.id))}
                            />
                        ))}
                    </ol>

                    {shown.length > PAGE && (
                        <button
                            type="button"
                            onClick={() => setExpanded((v) => !v)}
                            className="mt-2 rounded-lg font-sans text-sm font-medium text-ink-muted transition-colors duration-150 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                        >
                            {expanded ? "Réduire" : `Voir les ${shown.length} événements`}
                        </button>
                    )}
                </>
            )}
        </Section>
    );
}

function HistoryRow({ item: it, open, focused, onToggle }: { item: HistoryItem; open: boolean; focused: boolean; onToggle: () => void }) {
    const Icon = ICONS[it.kind];
    const meta = it.signal?.metadata;
    const hasDetail = it.signal?.type === "bodacc_announcement" && !!(meta?.company || meta?.founders?.length || meta?.buyer || meta?.previousOwner || meta?.administration?.raw);

    const head = (
        <>
            <span className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-4 ring-surface ${TONE_CHIP[it.tone]}`}>
                <Icon className="h-4 w-4" />
            </span>
            <span className="min-w-0 flex-1 pt-1">
                <span className="block font-sans text-sm font-medium text-ink">{it.title}</span>
                {it.detail && <span className="mt-0.5 block font-sans text-sm text-ink-muted">{it.detail}</span>}
            </span>
            <span className="flex shrink-0 items-center gap-1 pt-1 font-sans text-xs text-ink-faint">
                {fmtDate(it.date)}
                {hasDetail && (open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />)}
                {it.href && <ArrowUpRight className="h-4 w-4" />}
            </span>
        </>
    );

    const rowClass = `flex w-full items-start gap-3 rounded-lg py-2 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
        focused ? "bg-accent-subtle/60" : ""
    }`;

    return (
        <li id={`history-${it.id}`} className="scroll-mt-24">
            {it.href ? (
                <a href={it.href} target="_blank" rel="noopener noreferrer" className={`${rowClass} hover:bg-surface-sunken/60`}>{head}</a>
            ) : hasDetail ? (
                <button type="button" onClick={onToggle} aria-expanded={open} className={`${rowClass} hover:bg-surface-sunken/60`}>{head}</button>
            ) : (
                <div className={rowClass}>{head}</div>
            )}
            {open && hasDetail && <BodaccDetail meta={meta} />}
        </li>
    );
}

function BodaccDetail({ meta }: { meta: any }) {
    const known = (v?: string) => v && v !== "Unknown" ? v : null;
    const addr = meta.company?.address;
    return (
        <div className="mb-3 ml-11 space-y-4 rounded-lg bg-surface-sunken p-4 font-sans text-sm">
            {(meta.buyer || meta.previousOwner) && (
                <div className="grid grid-cols-2 gap-4">
                    {meta.previousOwner && (
                        <div>
                            <p className="text-xs text-ink-muted">Cédant</p>
                            <p className="font-medium text-ink">{meta.previousOwner.denomination || "-"}</p>
                            {meta.previousOwner.siren && <p className="font-mono text-xs text-ink-muted">{meta.previousOwner.siren}</p>}
                        </div>
                    )}
                    {meta.buyer && (
                        <div>
                            <p className="text-xs text-ink-muted">Acquéreur</p>
                            <p className="font-medium text-ink">{meta.buyer.denomination || "-"}</p>
                            {meta.buyer.siren && <p className="font-mono text-xs text-ink-muted">{meta.buyer.siren}</p>}
                            {meta.buyer.establishmentId && (
                                <a href={`/establishments/${meta.buyer.establishmentId}`} className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-ink hover:underline">
                                    Voir sa fiche <ArrowUpRight className="h-3 w-3" />
                                </a>
                            )}
                        </div>
                    )}
                </div>
            )}
            {meta.company && (
                <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                    {known(meta.company.denomination) && <Pair label="Dénomination" value={meta.company.denomination} />}
                    {known(meta.company.legalForm) && <Pair label="Forme juridique" value={meta.company.legalForm} />}
                    {meta.company.capital && <Pair label="Capital" value={`${meta.company.capital.amount} ${meta.company.capital.currency}`} />}
                    {known(meta.tribunal) && <Pair label="Greffe" value={meta.tribunal} />}
                    {addr?.city && (
                        <Pair
                            label="Siège"
                            value={`${[addr.streetNumber, addr.streetType, addr.streetName].filter(Boolean).join(" ")}${addr.postalCode ? `, ${addr.postalCode}` : ""} ${addr.city}`}
                            wide
                        />
                    )}
                </div>
            )}
            {meta.founders?.length > 0 && (
                <div>
                    <p className="mb-1 text-xs text-ink-muted">Dirigeants déclarés</p>
                    <ul className="space-y-0.5">
                        {meta.founders.map((f: any, i: number) => (
                            <li key={i} className="text-ink">
                                <span className="font-medium">{f.fullName || [f.prenom, f.nom].filter(Boolean).join(" ") || "-"}</span>
                                {f.qualite && <span className="text-ink-muted"> · {f.qualite}</span>}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {meta.administration?.raw && (
                <p className="leading-relaxed text-ink-muted">{meta.administration.raw}</p>
            )}
        </div>
    );
}

function Pair({ label, value, wide = false }: { label: string; value: string; wide?: boolean }) {
    return (
        <div className={wide ? "col-span-2" : ""}>
            <p className="text-xs text-ink-muted">{label}</p>
            <p className="text-ink">{value}</p>
        </div>
    );
}
